import { EvoluaApiError } from "@evolua/api-client";

export type ApiErrorDescription = {
  title: string;
  description: string;
  correlationId?: string;
};

function problemTraceId(error: EvoluaApiError): string | undefined {
  const problem = error.problem as { traceId?: string; correlationId?: string };
  const id = problem.traceId ?? problem.correlationId;
  return typeof id === "string" && id.length > 0 ? id : undefined;
}

/**
 * Copy de ErrorState alinhada a offline / cold start / Problem Details.
 */
export function describeApiError(
  error: unknown,
  options: { online?: boolean } = {},
): ApiErrorDescription {
  const online =
    options.online ?? (typeof navigator === "undefined" ? true : navigator.onLine);

  if (!online) {
    return {
      title: "Você está offline",
      description:
        "Sem conexão no momento. Reconecte e tente de novo — dados em cache podem aparecer na home.",
    };
  }

  if (error instanceof EvoluaApiError) {
    const code = error.problem.code;
    const correlationId = problemTraceId(error);

    if (code === "TIMEOUT") {
      return {
        title: "A API está demorando",
        description:
          error.problem.detail ||
          "Em ambiente free o serviço pode estar iniciando. Aguarde cerca de um minuto e tente de novo.",
        ...(correlationId ? { correlationId } : {}),
      };
    }
    if (code === "NETWORK_ERROR" || error.status === 0) {
      return {
        title: "Falha de rede",
        description:
          error.problem.detail ||
          "Não foi possível falar com a API. Verifique a conexão ou se o serviço está no ar.",
        ...(correlationId ? { correlationId } : {}),
      };
    }
    if (error.status === 502 || error.status === 503 || error.status === 504) {
      return {
        title: "Serviço indisponível",
        description:
          error.problem.detail ||
          "A API pode estar iniciando ou temporariamente fora. Tente de novo em instantes.",
        ...(correlationId ? { correlationId } : {}),
      };
    }

    return {
      title: error.problem.title || "Não foi possível carregar",
      description:
        error.problem.detail ||
        "Tente novamente. Se o problema continuar, fale com o suporte com o código abaixo.",
      ...(correlationId ? { correlationId } : {}),
    };
  }

  if (error instanceof Error && error.name === "AbortError") {
    return {
      title: "A API está demorando",
      description:
        "Em ambiente free o serviço pode estar iniciando. Aguarde cerca de um minuto e tente de novo.",
    };
  }

  return {
    title: "Não foi possível carregar",
    description: "Tente novamente. Se o problema continuar, fale com o suporte.",
  };
}
